<template>
    <div class="max-w-3xl mx-auto px-4 py-4 font-sans text-gray-800">

        <div class="mb-8">
            <NuxtLink to="/resources" class="text-secondary hover:text-secondary/80 flex items-center mb-4">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
                Resources
            </NuxtLink>
        </div>

        <img src="/images/resources/20-minute-home-audit.png" alt="20 Minute Home Audit"
            class="w-full h-64 object-cover rounded-xl shadow-md mb-8">


        <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 mb-8 leading-tight">The 20-Minute Home Audit:
            How to Document Your Wealth Before the Next Fire Season</h1>
        <p class="text-lg leading-relaxed mb-6">For homeowners in high-risk zones, "Fire Season" is no longer a seasonal
            event—it is a perennial threat. As the sky turns orange and evacuation warnings flicker on your phone, the
            last thing on your mind is the specific model number of your refrigerator or the brand of the suits in your
            closet.</p>

        <p class="text-lg leading-relaxed mb-6">Yet, this lack of documentation is exactly what leads to the "second
            disaster": an insurance settlement that covers only a fraction of what you’ve actually lost.</p>

        <p class="text-lg leading-relaxed mb-6">Most people procrastinate on home inventories because they imagine it
            requires a clipboard, a camera, and an entire lost weekend. It doesn't. By focusing on forensic-level proof
            for high-value items, you can complete a comprehensive home audit in 20 minutes. Here is how to document
            your wealth before the next ember flies.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Phase 1: Target the "Dispute
            Zones"</h2>
        <p class="text-lg leading-relaxed mb-6">Insurance adjusters rarely fight you on the big-ticket items like the
            roof or the framing; those are calculated by square footage. They fight you on Personal Property (Coverage
            C).</p>

        <p class="text-lg leading-relaxed mb-6">Adjusters are trained to default to the "Standard Grade" for any item
            that isn't specifically documented. If you list "5 Designer Handbags," they will pay out for five generic
            totes unless you have the forensic proof to show otherwise.</p>

        <h3 class="text-xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">High-Value Items Often Disputed:
        </h3>
        <p class="text-lg leading-relaxed mb-6">Electronics & Home Office: Professional-grade monitors, specialized
            gaming rigs, and high-end audio equipment.</p>

        <p class="text-lg leading-relaxed mb-6">Kitchen Assets: High-performance appliances (Vitamix, Wolf, Sub-Zero)
            and premium cookware sets.</p>

        <p class="text-lg leading-relaxed mb-6">Designer Goods: Apparel, watches, and handbags that carry significant
            brand-name value.</p>

        <p class="text-lg leading-relaxed mb-6">Collections: Rare books, wine, or hobby equipment (high-end bicycles or
            golf clubs).</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Phase 2: The Forensic Walkthrough
        </h2>
        <p class="text-lg leading-relaxed mb-6">To move through your home in 20 minutes, you must stop thinking like a
            cataloger and start thinking like a forensic auditor. You don't need to take 1,000 individual photos; you
            need a strategic series of "Evidence Clusters."</p>

        <h3 class="text-xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">1. The Wide-Angle Baseline
            (Minutes 1-5)
        </h3>
        <p class="text-lg leading-relaxed mb-6">Stand in the doorway of each room. Take 2–3 wide-angle photos that
            capture the entire space. This establishes the "baseline" of your lifestyle. These photos prove the quality
            of your flooring, the density of your furniture, and the general standard of your belongings. If you have a
            $10,000 rug, an adjuster needs to see it in its environment to validate its presence.</p>

        <h3 class="text-xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">2. The "Open-Door" Cluster
            (Minutes 6–15)</h3>
        <p class="text-lg leading-relaxed mb-6">This is where homeowners lose the most time—don't make the mistake of
            laying items out on a bed. Instead, use the Open-Door Method:</p>
        <ul class="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Closets:</strong> Open the doors and take 3–4 photos of the hanging racks and shelving. This
                proves the volume of clothing. Adjusters can identify brand quality (e.g., the silhouette of a tailored
                suit or a designer coat) from these shots.</li>
            <li><strong>Drawers:</strong> Pull out your kitchen, jewelry, or tech drawers. Take one clear top-down photo
                per drawer. This creates a "visual receipt" of your silverware, accessories, or components that is
                impossible to dispute.</li>
            <li><strong>Cabinets:</strong> For kitchens and bathrooms, open the cabinets and photograph the shelves
                as they are. This proves you own the "sets" (e.g., 12-piece knife block, full sets of glassware).</li>
        </ul>

        <h3 class="text-xl font-bold tracking-tight text-gray-900 mb-6 leading-tight">3. The Forensic Close-Up (Minutes
            16–20)</h3>
        <p class="text-lg leading-relaxed mb-6">Focus exclusively on items with a replacement value over $500. For
            these, you need "Audit-Ready" proof. Snap a clear, legible photo of:
        </p>

        <ul class="list-disc pl-6 mb-6 space-y-2">
            <li><strong>The Manufacturer Label:</strong> This is the silver sticker on the back of electronics or the
                rating plate on appliances. It contains the Model Number and Serial Number.</li>
            <li><strong>The Serial Number:</strong> This is the "DNA" of the item. It proves the exact age and model.
                For example, a "2023 Samsung QLED" is worth $1,500. A "2018 Samsung QLED" is worth $400. The serial
                number proves which one you have.</li>
            <li><strong>The Brand Badge:</strong> For designer furniture or high-end appliances, a close-up of the logo
                (e.g., Sub-Zero, Herman Miller, Sony) ensures you get paid for the premium brand you actually bought,
                not a generic equivalent.
            </li>
        </ul>

        <p class="text-lg leading-relaxed mb-6">Expert Note: In the eyes of an insurance carrier, a serial number is the
            ultimate form of "Audit-Ready" evidence. It eliminates all ambiguity regarding the age, model, and
            replacement value of the asset.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Why High-Resolution Photos Beat Video for Claims</h2>
        <p class="text-lg leading-relaxed mb-6">While video is easy to capture, it is often a nightmare for insurance
            adjusters to process. They cannot easily "zoom in" on a blurry frame to verify a serial number, and video
            files are often too large to be uploaded into their internal claims systems.</p>

        <p class="text-lg leading-relaxed mb-6">By using ClaimReady to convert your photos into a professional,
            high-resolution PDF, you are handing the adjuster exactly what they want: a static, searchable, and forensic
            document. You aren't just giving them footage; you are giving them evidence.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The Privacy Paradox: Why Most
            Inventory Apps are a Liability</h2>
        <p class="text-lg leading-relaxed mb-6">As you gather this data, you are essentially creating a digital
            blueprint of your wealth. This includes photos of your most expensive possessions, your home’s layout, and
            your address.</p>

        <p class="text-lg leading-relaxed mb-6">Most "modern" inventory apps ask you to upload this data to their cloud
            servers. This is a massive security risk. A single database breach could provide a "shopping list" for
            burglars or expose your most private financial information to the dark web.</p>

        <p class="text-lg leading-relaxed mb-6">Furthermore, many of these apps operate on a subscription model,
            charging you $10 a month just to keep your data accessible. You shouldn't have to pay a "privacy tax" to
            protect your own home.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">The Solution: Local-Only,
            Zero-Knowledge Documentation</h2>
        <p class="text-lg leading-relaxed mb-6">This is where the ClaimReady philosophy changes the game. We believe
            that your data should be your eyes only.</p>

        <p class="text-lg leading-relaxed mb-6">ClaimReady is a Zero-Knowledge utility. When you use our tool to
            generate your inventory, nothing is ever uploaded to a server. Your photos, your serial numbers, and your
            high-resolution PDF report are processed locally in your browser.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Why ClaimReady is the Gold
            Standard for Fire Season:</h2>
        <p class="text-lg leading-relaxed mb-6">No Accounts: No email or password to be leaked in a data breach.</p>

        <p class="text-lg leading-relaxed mb-6">Local Processing: Your photos never leave your device.</p>

        <p class="text-lg leading-relaxed mb-6">Audit-Ready Output: You receive a professional, forensic-grade PDF
            designed to meet the evidentiary standards of the most skeptical adjusters.</p>

        <p class="text-lg leading-relaxed mb-6">One-Time Utility: No subscriptions. For a single $6.99 payment, you get
            a tool that can protect $20,000 to $200,000+ in claim value.</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-12 mb-4">Conclusion: 20 Minutes vs. Two
            Years of Regret</h2>
        <p class="text-lg leading-relaxed mb-6">Disasters are unpredictable, but your financial recovery doesn't have to
            be. If a fire hits tomorrow, do you want to spend the next two years arguing with an adjuster about whether
            your couch was from IKEA or a custom boutique?</p>

        <p class="text-lg leading-relaxed mb-6">Take the 20 minutes. Perform the audit. Keep the data private.</p>

        <UCard class="text-lg font-semibold">Don't leave your recovery to chance. Use ClaimReady today to generate
            your audit-ready inventory for a one-time cost of $6.99.</UCard>

        <div class="mt-8 flex justify-center">
            <UButton label="Get Started" to="/" size="xl" />
        </div>
    </div>
</template>

<script setup lang="ts">
definePageMeta({
    layout: 'default'
})
useHead({
    title: 'The 20-Minute Home Audit: Document Wealth Before Fire Season',
    meta: [
        {
            name: 'title',
            content: 'The 20-Minute Home Audit: Document Wealth Before Fire Season'
        },
        {
            name: 'description',
            content: `Complete a forensic home inventory in 20 minutes. Learn how to document high-value items for insurance claims before fire season hits, keeping your data 100% private.`
        },
        {
            name: 'keywords',
            content: 'documenting valuables for insurance, home insurance claim tips, fire season preparation, private home inventory, zero-knowledge apps, audit-ready inventory'
        },
        {
            name: 'robots',
            content: 'index, follow'
        },
        {
            name: 'language',
            content: 'English'
        },
        {
            name: 'author',
            content: 'ClaimReady'
        },
        {
            name: 'copyright',
            content: 'ClaimReady'
        },
        {
            name: 'og:title',
            content: `The 20-Minute Home Audit: Is Your Wealth Protected?`
        },
        {
            name: 'og:description',
            content: `Don't let the 'second disaster' of a lowball insurance settlement ruin your recovery. Follow our 20-minute forensic guide to document your home privately.`
        },
        {
            name: 'og:type',
            content: 'article'
        },
        {
            name: 'og:url',
            content: 'https://getclaimready.app/resources/20-minute-home-audit'
        },
        {
            name: 'twitter:card',
            content: 'summary_large_image'
        },
        {
            name: 'twitter:title',
            content: '20 Minutes to Protect $20k+: The Pre-Fire Season Audit'
        },
        {
            name: 'twitter:description',
            content: `Stop procrastinating on your home inventory. Our forensic walkthrough ensures you're audit-ready without ever uploading your private data to the cloud.`
        },
        {
            name: 'og:image',
            content: 'https://getclaimready.app/images/resources/20-minute-home-audit.png'
        },
        {
            name: 'twitter:image',
            content: 'https://getclaimready.app/images/resources/20-minute-home-audit.png'
        },
    ],
    link: [
        {
            rel: 'canonical',
            href: 'https://getclaimready.app/resources/20-minute-home-audit'
        }
    ]
})
</script>